import { Auth } from 'aws-amplify'
import { useState, useEffect, useMemo, useCallback } from 'react'
import http, {
	COGNITO_ACCESS_TOKEN_PATTERN_KEY,
	COGNITO_ID_TOKEN_PATTERN_KEY,
	COGNITO_LAST_USER_PATTERN_KEY,
	REDIRECT_URL_KEY,
	REFERRAL_KEY,
	TOKEN_KEY,
} from 'api/http'
import { useCurrentUser, useOnboarding } from 'api'
import { subscribeToAppsyncEvent } from './helpers'
import { ISubscribeAppSyncEvent } from './models'
import appRoutes from 'modules/common/app-routes'
import { useRouter } from 'next/router'
import { useQueryClient } from 'react-query'
import queries from 'api/queries'
import { message } from 'antd'
import { useLocalization } from 'localization'
import { getReportBySlug } from 'api/reports/services'
import { useDebouncedCallback } from 'use-debounce'
import { useAppContext } from 'modules/common/appContext'

const publicRoutes: string[] = []

export const useSessionProvider = () => {
	const isPublicRoute = useMemo(
		() => (typeof window !== 'undefined' ? publicRoutes.includes(window.location.pathname) : false),
		[]
	)
	const [authorized, setAuthorized] = useState(isPublicRoute)
	const hasToken = useMemo(() => !!http.getStorageItem(TOKEN_KEY), [authorized])
	const { data: onboardingStatus, isFetched: isOnboardingFetched } = useOnboarding(hasToken)
	const [redirectInProgress, setRedirectInProgress] = useState(false)
	const router = useRouter()
	const { isWealthicaContext } = useAppContext()

	useEffect(() => {
		if (onboardingStatus?.finished === false && router.pathname !== appRoutes.auth.onboarding()) {
			if (!isWealthicaContext) setRedirectInProgress(true)
			router.push(appRoutes.auth.onboarding()).finally(() => setRedirectInProgress(false))
			return
		}

		if (onboardingStatus?.finished === true && router.pathname === appRoutes.auth.onboarding()) {
			if (!isWealthicaContext) setRedirectInProgress(true)
			setRedirectInProgress(true)
			router.push(appRoutes.dashboard()).finally(() => setRedirectInProgress(false))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onboardingStatus])

	useEffect(() => {
		if (!isPublicRoute) {
			const query = new URLSearchParams(window.location.search.substring(1))
			const referral = query.get('referral')

			if (typeof referral === 'string' && !http.getStorageItem(REFERRAL_KEY) && referral !== '') {
				http.setStorageItem(REFERRAL_KEY, referral)
			}

			Auth.currentSession()
				.then(session => {
					const accessToken = session.getAccessToken().getJwtToken()
					http.setStorageItem(TOKEN_KEY, accessToken)
					const lastAuthUser = localStorage.getItem(COGNITO_LAST_USER_PATTERN_KEY)
					setAuthorized(!!accessToken)
					window.opener?.childAuthorized?.({
						idToken:
							lastAuthUser && localStorage.getItem(COGNITO_ID_TOKEN_PATTERN_KEY.replace('{ACCOUNT}', lastAuthUser)),
						accessToken:
							lastAuthUser && localStorage.getItem(COGNITO_ACCESS_TOKEN_PATTERN_KEY.replace('{ACCOUNT}', lastAuthUser)),
						lastAuthUser,
					})
				})
				.catch(() => {
					if (router.pathname !== appRoutes.dashboard() && router.pathname !== appRoutes.auth.authgrantRedirect()) {
						http.setStorageItem(REDIRECT_URL_KEY, window.location.href)
					}
				})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {
		allowedRender: isOnboardingFetched && !redirectInProgress,
		isOnboarding: router.pathname === appRoutes.auth.onboarding(),
	}
}

export const useAppSyncEvents = () => {
	const { data: user } = useCurrentUser()
	const queryClient = useQueryClient()
	const { t } = useLocalization()
	const deboucedReportLoading = useDebouncedCallback((reportId: string, taxYear: number) => {
		getReportBySlug(reportId, taxYear).then(({ progress, url }) => {
			if (progress === 1 && url) {
				window.open(url, '_blank')
			}
		})
	}, 1000)

	const onEventReceive: ISubscribeAppSyncEvent['onReceive'] = useCallback(({ value }) => {
		console.log(value)
		switch (value.data?.getUpdatedMainEvent.eventType) {
			case 'INTEGRATION':
				queryClient.invalidateQueries([queries.transactions])
				queryClient.invalidateQueries([queries.transactionById])
				queryClient.invalidateQueries([queries.transactionStatistic])
				queryClient.invalidateQueries([queries.integrations])
				queryClient.invalidateQueries([queries.wallets])
				queryClient.invalidateQueries([queries.integrationById, value.data.getUpdatedMainEvent.payload?.id])
				queryClient.invalidateQueries([queries.analyzeResults])
				queryClient.invalidateQueries([queries.analyzeStatus])
				queryClient.invalidateQueries([queries.featureAccess])
				break
			case 'PRICES_RECALCULATION':
				queryClient.invalidateQueries([queries.recalculations])
				queryClient.invalidateQueries([queries.analyzeResults])
				queryClient.invalidateQueries([queries.analyzeStatus])
				queryClient.invalidateQueries([queries.featureAccess])
				break
			case 'REMOVE_ALL_TRANSACTIONS':
				queryClient.invalidateQueries([queries.transactionById])
				queryClient.invalidateQueries([queries.transactions])
				queryClient.invalidateQueries([queries.transactionStatistic])
				queryClient.invalidateQueries([queries.analyzeResults])
				queryClient.invalidateQueries([queries.analyzeStatus])
				queryClient.invalidateQueries([queries.featureAccess])
				queryClient.invalidateQueries([queries.integrationById])
				queryClient.invalidateQueries([queries.integrations])
				queryClient.invalidateQueries([queries.wallets])
				queryClient.invalidateQueries([queries.integrationStatistic])
				message.success(t('transactions.removeAll.finish'))
				break
			case 'REPORT': {
				queryClient.invalidateQueries([queries.reportBySlug])
				queryClient.invalidateQueries([queries.reports])
				queryClient.invalidateQueries([queries.featureAccess])
				const reportId = value.data.getUpdatedMainEvent.payload?.id
				const taxYear = value.data.getUpdatedMainEvent.payload?.taxYear

				if (reportId && taxYear) {
					deboucedReportLoading(reportId, taxYear)
				}
				break
			}
			case 'TAX_ANALYZE':
				queryClient.invalidateQueries([queries.analyzeResults])
				queryClient.invalidateQueries([queries.analyzeStatus])
				queryClient.invalidateQueries([queries.featureAccess])
				queryClient.invalidateQueries([queries.transactionById])
				queryClient.invalidateQueries([queries.transactions])
				queryClient.invalidateQueries([queries.transactionStatistic])
				queryClient.invalidateQueries([queries.organizationById])
				break
			case 'TRANSFER_MAPPING':
				queryClient.invalidateQueries([queries.transactionById])
				queryClient.invalidateQueries([queries.transactions])
				queryClient.invalidateQueries([queries.transactionStatistic])
				queryClient.invalidateQueries([queries.analyzeResults])
				queryClient.invalidateQueries([queries.analyzeStatus])
				queryClient.invalidateQueries([queries.featureAccess])
				if (value.data?.getUpdatedMainEvent.payload?.transfersCreated !== undefined) {
					message.success(
						t('transactions.transfersAutoCombine.finish', {
							transctions: value.data.getUpdatedMainEvent.payload.transfersCreated,
						})
					)
				}
				break
			default:
				break
		}
	}, [])

	useEffect(() => {
		let eventSubscription: ReturnType<typeof subscribeToAppsyncEvent>
		if (user?.id) {
			const tryToSubscribe = () => {
				eventSubscription = subscribeToAppsyncEvent({
					userId: user.id,
					onReceive: onEventReceive,
					reconnect: tryToSubscribe,
				})
			}
			tryToSubscribe()
		}

		return () => {
			if (eventSubscription) eventSubscription.unsubscribe()
		}
	}, [user?.id])
}
